import { AxiosError, type AxiosResponse, isAxiosError } from 'axios'

import type { ArkindexConflictErrorResponse, ArkindexErrorResponse } from '@/types'

type RequiredResponseAxiosError<T> = AxiosError<T> & { response: AxiosResponse<T> }

/**
 * Checks whether an error is an AxiosError caused by an Arkindex standardized error response.
 */
export function isArkindexError(
  error: unknown,
): error is RequiredResponseAxiosError<ArkindexErrorResponse> {
  return (
    error !== null &&
    isAxiosError(error) &&
    !!error.response &&
    error.response.status >= 400 &&
    error.response.status < 600 &&
    !!error.response.data &&
    ['client_error', 'server_error', 'validation_error'].includes(error.response.data.type) &&
    Array.isArray(error.response.data.errors)
  )
}

export function isArkindexConflictError(
  error: unknown,
): error is RequiredResponseAxiosError<ArkindexConflictErrorResponse> {
  return (
    isArkindexError(error) &&
    error.response.status === 409 &&
    error.response.data.type === 'client_error' &&
    error.response.data.errors.length > 0 &&
    error.response.data.errors.some((err) => err.code === 'conflict') &&
    'existing_id' in error.response.data
  )
}

/**
 * A helper method to try to parse errors into human-readable strings.
 * This also parses anything that is not an Error, since JavaScript allows throwing anything, not just errors.
 *
 * @param error Any error.
 * @returns A string representing the error in a way as human-readable as possible.
 */
export const errorParser = (error: unknown): string => {
  if (typeof error === 'string') return error

  /*
   * There are two types of strings in JS!
   * We can't call null.toString() or undefined.toString(), but we can call String(null).toString()…
   */
  if (!error) return String(error).toString()

  let message = null
  if (isAxiosError(error) && error.response?.data) {
    // Basic support for Arkindex standard errors: use all `errors[].detail` attributes
    if (isArkindexError(error) && error.response.data.errors.length) {
      message = error.response.data.errors.map(({ detail }) => detail)
    } else if (
      typeof error.response.data === 'string' ||
      error.response.data instanceof String ||
      Array.isArray(error.response.data)
    )
      message = error.response.data
    else if (error.response.data.__all__) message = error.response.data.__all__
    else if (error.response.data.detail) message = error.response.data.detail
    else if (error.response.data.non_field_errors) message = error.response.data.non_field_errors
  }

  if (!message && error instanceof Error && error.message) message = error.message
  if (!message) return String(error).toString()

  if (Array.isArray(message)) return message.join(' - ')
  if (typeof message === 'string') return message
  return message.toString()
}

/**
 * Extract errors for specific fields from a standard Arkindex API error response,
 * and return those along all other error messages grouped into a single array.
 * @param response Arkindex API error response.
 * @param fields Field names to extract.
 *   Errors for attributes nested into those fields, like on an array value, will be merged into the parent field.
 * @returns A tuple of field errors, as arrays of strings for each field name, and all other errors as one string array.
 */
export function extractFieldErrors<T extends string>(
  response: ArkindexErrorResponse,
  fields: T[],
): [fieldErrors: { [k in T]?: string[] | undefined }, otherErrors: string[]] {
  const fieldErrors: { [k in T]?: string[] | undefined } = {}
  const otherErrors: string[] = []

  for (const error of response.errors) {
    if (
      error.attr?.length &&
      typeof error.attr[0] === 'string' &&
      (fields as string[]).includes(error.attr[0])
    ) {
      const key = error.attr[0] as T
      if (fieldErrors[key]) fieldErrors[key].push(error.detail)
      else fieldErrors[key] = [error.detail]
    } else {
      otherErrors.push(error.detail)
    }
  }

  return [fieldErrors, otherErrors]
}
